import { ThreeDCardPreview } from '@sendoutcards/editor'
import React from 'react'
import { CardFragment } from 'src/graphql/generated/graphql'
import {
  get3DCardPreviewStandardProps,
  mapPanelControlNames,
} from 'src/helpers/threeDCard/mapPanelImages'
import { useActions, useSelector } from 'src/hooks'
import { useFlags } from 'src/react_query/queries/hooks'
import styles from './actPreview.module.scss'
import { Button } from 'src/design_system/molecules/button/Button'
import { Icon, Text } from '@sendoutcards/quantum-design-ui'
import { Icon as DSIcon } from 'src/design_system/atoms/icons/components/Icon'

type ActPreviewProps = {
  card: CardFragment
  shouldShowPreview: boolean
  /**
   * This can be used to have dynamic height when in mobile mode
   * e.g: if set to initial it should have the size of the content
   * with a max height of 80dvh
   */
  mobileSetupPanelHeight?: string
  togglePreviewOrder: () => void
}

const PREVIEW_CONTROL_ID = 'previewControlId'

const ActPreview: React.FC<ActPreviewProps> = props => {
  const {
    card,
    shouldShowPreview,
    children,
    mobileSetupPanelHeight,
    togglePreviewOrder,
  } = props
  const isDesktop = useSelector(state => state.window.width >= 800)
  const actions = useActions()

  const flags = useFlags()
  const canViewEnvelopeAnimation = flags.data?.some(
    flag => flag.name === 'can_view_envelope_animation',
  )

  const controlPanelNames = mapPanelControlNames(card.panels)

  const handleBackToEditor = () => {
    actions.openCard(card.id)
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.threeDPreview} ${!isDesktop ? styles.mobilePreview : ''} ${shouldShowPreview ? styles.previewOpen : ''}`}
      >
        <ThreeDCardPreview
          {...get3DCardPreviewStandardProps(card)}
          height="calc(100dvh - 350px)"
          control={{
            type: 'Portal',
            portalId: PREVIEW_CONTROL_ID,
            panelNames: controlPanelNames,
            shouldOnlyShowDropdown: !isDesktop,
          }}
          bgGradientColor={{ shouldDisplayBg: true }}
          shouldDisplayScene={canViewEnvelopeAnimation}
          shouldOpenEnvelope={shouldShowPreview || isDesktop}
        />
        <div id={PREVIEW_CONTROL_ID} className={styles.previewControl} />
      </div>
      <div>
        <div className={styles.backToEditor}>
          <Button
            padding="8px 14px 8px 6px"
            borderRadius="0.875rem"
            fill="#FFF"
            boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.08)"
            onClick={handleBackToEditor}
            gap={'x0'}
          >
            <Icon name="leftChevron" size="small" primaryColor="#6B7280" />
            <Text
              type="body"
              content="Editor"
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#404040',
              }}
            />
          </Button>
        </div>
        {!isDesktop && (
          <>
            {shouldShowPreview ? (
              <div className={styles.showOrderBtn}>
                <Button
                  padding="8px 12px 8px 10px"
                  borderRadius="0.875rem"
                  fill="#EBFFEE"
                  boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.08)"
                  onClick={togglePreviewOrder}
                  gap={'x_5'}
                >
                  <DSIcon name="send" width={20} height={20} fill="#44A263" />
                  <Text
                    type="body"
                    content="Send"
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      color: '#44A263',
                    }}
                  />
                </Button>
              </div>
            ) : (
              <div className={styles.showOrderBtn}>
                <Button
                  padding="8px 12px"
                  borderRadius="0.875rem"
                  fill="#FFF"
                  boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.08)"
                  onClick={togglePreviewOrder}
                >
                  <Text
                    type="body"
                    content="Preview"
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      color: '#404040',
                    }}
                  />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <div
        className={`${styles.sidePanel} ${!isDesktop ? styles.mobileSidePanel : ''} ${shouldShowPreview ? styles.previewOpen : ''}`}
        style={
          mobileSetupPanelHeight
            ? { height: mobileSetupPanelHeight }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  )
}

export default ActPreview
